import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  Box,
  TextField,
  Button,
  Stack,
  Alert
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ReactApexChart from "react-apexcharts";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import type { ApexOptions, ApexAxisChartSeries } from "apexcharts";
import WhoLineChart from "./charts/WhoLineChart";
import CumulativeTrendChart from "./charts/CumulativeTrendChart";
import { calculateBodyMetrics, calculateAgeInYears } from "../utils/bodyMetrics";

interface Props {
  open: boolean;
  onClose: () => void;
  row: any;
  measurements: TrendMeasurement[];
}

export interface TrendMeasurement {
  date: string;
  height: number;
  weight: number;
  heartRate: number;
  bmi: number;
  bodyFat?: number;
  muscleMass?: number;
  boneMass?: number;
  bmr?: number;
  protein?: number;
  bodyMoisture?: number;
  visceralFat?: number;
  subcutaneousFat?: number;
  leanBodyMass?: number;
  standardWeight?: number;
  name?: string;
  studentId?: number;
}

const StudentTrendChartDialog: React.FC<Props> = ({
  open,
  onClose,
  row,
  measurements
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const [chartReady, setChartReady] = useState(false);

  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => setChartReady(true), 50);
      return () => clearTimeout(timer);
    } else {
      setChartReady(false);
    }
  }, [open]);

  const parameterOptions = [
    { label: "Cumulative Trend", key: "cumulative", unit: "" },
    { label: "Height (cm)", key: "height", unit: "cm" },
    { label: "Weight (kg)", key: "weight", unit: "kg" },
    { label: "BMI", key: "bmi", unit: "kg/m²" },
    { label: "Heart Rate (bpm)", key: "heartRate", unit: "bpm" },
    { label: "Body Fat (%)", key: "bodyFat", unit: "%" },
    { label: "Muscle Mass (kg)", key: "muscleMass", unit: "kg" },
    { label: "Bone Mass (kg)", key: "boneMass", unit: "kg" },
    { label: "Pediatric BMR Estimate (kcal)", key: "bmr", unit: "kcal" },
    { label: "Protein (%)", key: "protein", unit: "%" },
    { label: "Body Moisture (%)", key: "bodyMoisture", unit: "%" },
    { label: "Visceral Index (Estimated)", key: "visceralFat", unit: "rating" },
    { label: "Subcutaneous Fat (%)", key: "subcutaneousFat", unit: "%" },
    { label: "Lean Body Mass (kg)", key: "leanBodyMass", unit: "kg" },
    { label: "Reference Median Weight (P50)", key: "standardWeight", unit: "kg" }
  ];

  const [selectedParameter, setSelectedParameter] = useState(parameterOptions[0].label);
  const [exportOpen, setExportOpen] = useState(false);

  const [selectedCharts, setSelectedCharts] = useState<Record<string, boolean>>(
    Object.fromEntries(parameterOptions.map(p => [p.label, true]))
  );

  if (!row || !measurements?.length) return null;

  /* ------------------ DATA PREPARATION & STRICT FILTERING ------------------ */

  // 1. Strictly filter measurements to only this student
  const targetStudentId = row.studentId || row.id;
  const targetName = (row.name || "").trim().toLowerCase();

  const filtered = measurements.filter(m => {
    const mStudentId = m.studentId;
    const mName = (m.name || "").trim().toLowerCase();

    if (targetStudentId && mStudentId) {
      return String(mStudentId) === String(targetStudentId);
    }
    if (targetName && targetName !== "-") {
      return mName === targetName;
    }
    return true;
  });

  const studentDataList = filtered.length > 0 ? filtered : [row];

  // 2. Enrich each measurement with calculated body metrics and sort chronologically ASC
  const enrichedMeasurements = studentDataList
    .map(m => {
      const measurementDate = m.date || row.lastMeasurement || row.measurementDate || new Date().toISOString();
      const metrics = calculateBodyMetrics(
        Number(m.height || row.height || 0),
        Number(m.weight || row.weight || 0),
        row.dob || "",
        row.gender || "Male",
        Number(m.heartRate || row.heartRate || 70),
        measurementDate
      );

      return {
        ...m,
        ...metrics,
        date: measurementDate,
        height: Number(m.height || row.height || 0),
        weight: Number(m.weight || row.weight || 0),
        heartRate: Number(m.heartRate || row.heartRate || 0),
        bmi: typeof metrics.bmi === "number" ? metrics.bmi : Number(m.bmi || 0),
        bodyFat: typeof metrics.bodyFat === "number" ? metrics.bodyFat : Number(m.bodyFat || 0),
        muscleMass: typeof metrics.muscleMass === "number" ? metrics.muscleMass : Number(m.muscleMass || 0),
        boneMass: typeof metrics.boneMass === "number" ? metrics.boneMass : Number(m.boneMass || 0),
        bmr: typeof metrics.bmr === "number" ? metrics.bmr : Number(m.bmr || 0),
        protein: typeof metrics.protein === "number" ? metrics.protein : Number(m.protein || 0),
        bodyMoisture: typeof metrics.bodyMoisture === "number" ? metrics.bodyMoisture : Number(m.bodyMoisture || 0),
        visceralFat: typeof metrics.visceralFat === "number" ? metrics.visceralFat : Number(m.visceralFat || 0),
        subcutaneousFat: typeof metrics.subcutaneousFat === "number" ? metrics.subcutaneousFat : Number(m.subcutaneousFat || 0),
        leanBodyMass: typeof metrics.leanBodyMass === "number" ? metrics.leanBodyMass : Number(m.leanBodyMass || 0),
        standardWeight: typeof metrics.standardWeight === "number" ? metrics.standardWeight : Number(m.standardWeight || 0)
      };
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  const isSingleRecord = enrichedMeasurements.length === 1;
  const currentOption = parameterOptions.find(p => p.label === selectedParameter) || parameterOptions[0];

  // 3. Determine metric values
  const currentMetricValues = enrichedMeasurements.map(m => {
    const val = (m as any)[currentOption.key];
    return typeof val === "number" ? val : Number(val) || 0;
  });

  const hasValidData = currentMetricValues.some(v => v > 0);

  // 4. Chronological categories with individual measurement dates (Day Mon Year)
  const dateCategories = enrichedMeasurements.map(m => {
    const d = new Date(m.date);
    if (isNaN(d.getTime())) return m.date || "-";
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  });

  // 5. Series for single metric charts
  const singleSeries: ApexAxisChartSeries = [
    {
      name: selectedParameter,
      data: currentMetricValues
    }
  ];

  const yMin = Math.min(...currentMetricValues);
  const yMax = Math.max(...currentMetricValues);
  const yPadding = yMax > yMin ? (yMax - yMin) * 0.15 : (yMax || 10) * 0.15;

  const singleChartOptions: ApexOptions = {
    chart: {
      type: "line",
      fontFamily: "Inter, Roboto, 'Segoe UI', sans-serif",
      animations: {
        enabled: false
      },
      toolbar: {
        show: true,
        tools: {
          download: false,
          selection: true,
          zoom: true,
          zoomin: true,
          zoomout: true,
          pan: true,
          reset: true
        }
      },
      zoom: { enabled: true }
    },
    colors: ["#0284C7"],
    stroke: { curve: "smooth", width: isSingleRecord ? 0 : 3.5 },
    markers: {
      size: isSingleRecord ? 9 : 6,
      colors: ["#0284C7"],
      strokeColors: "#FFFFFF",
      strokeWidth: 3,
      hover: { size: 10 }
    },
    xaxis: {
      categories: dateCategories,
      labels: {
        rotate: dateCategories.length > 3 ? -35 : 0,
        rotateAlways: dateCategories.length > 5,
        style: { fontSize: "11px", fontWeight: 500, colors: "#64748B" }
      },
      title: {
        text: "Measurement Timeline (Chronological)",
        style: { fontWeight: 600, color: "#1E293B", fontSize: "12px" }
      },
      axisBorder: { show: true, color: "#E2E8F0" },
      axisTicks: { show: true, color: "#E2E8F0" }
    },
    yaxis: {
      min: Math.max(0, Math.floor(yMin - yPadding)),
      max: Math.ceil(yMax + yPadding) || 10,
      title: {
        text: selectedParameter,
        style: { fontWeight: 600, color: "#1E293B", fontSize: "12px" }
      },
      axisBorder: { show: true, color: "#E2E8F0" },
      axisTicks: { show: true, color: "#E2E8F0" },
      labels: {
        style: { colors: "#64748B", fontSize: "11px", fontWeight: 500 },
        formatter: val => `${Math.round(val)} ${currentOption.unit}`
      }
    },
    grid: {
      show: true,
      borderColor: "#F1F5F9",
      strokeDashArray: 3,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } }
    },
    tooltip: {
      shared: false,
      intersect: false,
      custom: ({ dataPointIndex }) => {
        const dateStr = dateCategories[dataPointIndex] || "-";
        const val = currentMetricValues[dataPointIndex];
        return `
          <div style="padding: 12px 16px; font-family: Inter, Roboto, sans-serif; font-size: 12px; line-height: 1.6; background: rgba(255, 255, 255, 0.98); backdrop-filter: blur(8px); border: 1px solid #0284C7; border-radius: 10px; box-shadow: 0 10px 25px -5px rgba(2, 132, 199, 0.2), 0 8px 10px -6px rgba(0, 0, 0, 0.08);">
            <div style="font-weight: 700; color: #0F172A; margin-bottom: 4px; border-bottom: 1px solid #F1F5F9; padding-bottom: 4px; display: flex; justify-content: space-between; gap: 12px;">
              <span>${row.name || "Student"}</span>
              <span style="font-size: 10px; font-weight: 600; padding: 2px 6px; background: #E0F2FE; color: #0284C7; border-radius: 4px;">Recorded</span>
            </div>
            <div style="color: #475569; display: flex; justify-content: space-between; gap: 12px;"><span>Date:</span> <b style="color: #1E293B;">${dateStr}</b></div>
            <div style="margin-top: 6px; padding-top: 4px; border-top: 1px dashed #E2E8F0; display: flex; justify-content: space-between; align-items: center; gap: 12px;">
              <span style="color: #0284C7; font-weight: 600;">${selectedParameter}:</span>
              <b style="font-size: 14px; color: #0284C7;">${val} ${currentOption.unit}</b>
            </div>
          </div>
        `;
      }
    }
  };

  // 6. WHO Chart Data
  const isWHOChart =
    selectedParameter === "Height (cm)" ||
    selectedParameter === "Weight (kg)" ||
    selectedParameter === "BMI";

  const studentWHOData = enrichedMeasurements.map(m => {
    const exactAge = calculateAgeInYears(row.dob, m.date);
    const safeAge = exactAge > 0 ? exactAge : (Number(row.age) || 5);
    return {
      age: safeAge,
      value:
        selectedParameter === "Height (cm)"
          ? m.height
          : selectedParameter === "Weight (kg)"
          ? m.weight
          : m.bmi,
      date: m.date,
      name: row.name,
      gender: row.gender
    };
  });

  /* ------------------ EXPORT ALL PDF ------------------ */

  const handleExportAllPDF = async () => {
    const pdf = new jsPDF("landscape", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = pageWidth - 20;
    const imgHeight = 90;
    let yPosition = 15;

    // Add Title on first page
    pdf.setFontSize(16);
    pdf.text(`${row.name || "Student"} — Physical Measurement Reports`, 10, 10);

    for (const param of parameterOptions) {
      if (!selectedCharts[param.label]) continue;

      setSelectedParameter(param.label);
      await new Promise(r => setTimeout(r, 600));

      if (!chartRef.current) continue;

      const canvas = await html2canvas(chartRef.current, {
        scale: 1.5,
        backgroundColor: "#ffffff"
      });

      const img = canvas.toDataURL("image/png");

      if (yPosition + imgHeight > pageHeight - 10) {
        pdf.addPage();
        yPosition = 15;
      }

      pdf.addImage(img, "PNG", 10, yPosition, imgWidth, imgHeight);
      yPosition += imgHeight + 8;
    }

    pdf.save(`${row.name || "Student"}-Health-Charts.pdf`);
    setExportOpen(false);
  };

  /* ------------------ RENDER ------------------ */

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 20px 25px -5px rgba(15, 23, 42, 0.15), 0 8px 10px -6px rgba(15, 23, 42, 0.1)"
        }
      }}
      TransitionProps={{
        onEntered: () => setChartReady(true),
        onExited: () => setChartReady(false)
      }}
    >
      <DialogTitle component="div" sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2.5, borderBottom: "1px solid #F1F5F9" }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: "#0F172A", letterSpacing: "-0.01em" }}>
          {row.name} – Health & Measurement Trends
        </Typography>

        <Box display="flex" alignItems="center" gap={1.5}>
          <TextField
            select
            size="small"
            value={selectedParameter}
            onChange={e => setSelectedParameter(e.target.value)}
            SelectProps={{ native: true }}
            sx={{
              minWidth: 240,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: "13px",
                fontWeight: 500
              }
            }}
          >
            {parameterOptions.map(p => (
              <option key={p.key} value={p.label}>
                {p.label}
              </option>
            ))}
          </TextField>

          <Button
            size="small"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={() => setExportOpen(true)}
            sx={{
              borderColor: "#CBD5E1",
              color: "#334155",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              "&:hover": { borderColor: "#94A3B8", bgcolor: "#F8FAFC" }
            }}
          >
            Export PDF
          </Button>

          <IconButton onClick={onClose} size="small" sx={{ color: "#64748B" }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ minHeight: 480, p: 2.5, bgcolor: "#FFFFFF" }}>
        {/* Informative banner if only 1 historical record exists */}
        {isSingleRecord && (
          <Alert
            severity="info"
            icon={<InfoOutlinedIcon fontSize="inherit" />}
            sx={{
              mb: 2,
              backgroundColor: "#EFF6FF",
              color: "#1E3A8A",
              borderRadius: 2,
              border: "1px solid #BFDBFE"
            }}
          >
            Only 1 measurement recorded for <strong>{row.name}</strong> on{" "}
            <strong>
              {new Date(enrichedMeasurements[0].date).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric"
              })}
            </strong>
            . Additional measurements added over time will automatically connect as a multi-point trend line.
          </Alert>
        )}

        {open && chartReady ? (
          <div ref={chartRef}>
            {selectedParameter === "Cumulative Trend" ? (
              <CumulativeTrendChart
                key={`cum-${targetStudentId}`}
                measurements={enrichedMeasurements}
                studentName={row.name}
              />
            ) : isWHOChart ? (
              <WhoLineChart
                key={`who-${targetStudentId}-${selectedParameter}-${row.gender}`}
                title={selectedParameter as any}
                gender={row.gender || "Male"}
                studentName={row.name || "Student"}
                studentData={studentWHOData}
                unit={currentOption.unit}
              />
            ) : hasValidData ? (
              <Box sx={{ mt: 1 }}>
                <ReactApexChart
                  key={`single-${targetStudentId}-${selectedParameter}`}
                  options={singleChartOptions}
                  series={singleSeries}
                  type="line"
                  height={420}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  py: 10,
                  textAlign: "center",
                  bgcolor: "#fafafa",
                  borderRadius: 2,
                  border: "1px dashed #cccccc",
                  mt: 3
                }}
              >
                <Typography variant="h6" component="div" color="text.secondary" fontWeight={500}>
                  No data available for this metric.
                </Typography>
                <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
                  There are no recorded measurements for {selectedParameter} for this student yet.
                </Typography>
              </Box>
            )}
          </div>
        ) : (
          <Box sx={{ minHeight: 420, display: "flex", alignItems: "center", justifyContent: "center" }} />
        )}
      </DialogContent>

      {/* -------- EXPORT SELECTION DIALOG -------- */}
      <Dialog open={exportOpen} onClose={() => setExportOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle component="div" sx={{ fontWeight: 600, color: "#1F497D" }}>Select Charts to Export</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {parameterOptions.map(p => (
              <Box key={p.label} display="flex" alignItems="center">
                <input
                  type="checkbox"
                  id={`export-${p.key}`}
                  checked={!!selectedCharts[p.label]}
                  onChange={e =>
                    setSelectedCharts(s => ({
                      ...s,
                      [p.label]: e.target.checked
                    }))
                  }
                  style={{ width: 16, height: 16, accentColor: "#1F497D" }}
                />
                <Typography component="label" htmlFor={`export-${p.key}`} ml={1.5} variant="body2" sx={{ cursor: "pointer" }}>
                  {p.label}
                </Typography>
              </Box>
            ))}
          </Stack>

          <Box mt={3} display="flex" justifyContent="flex-end" gap={1.5}>
            <Button onClick={() => setExportOpen(false)} color="inherit" size="small">
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleExportAllPDF}
              size="small"
              sx={{ backgroundColor: "#1F497D", textTransform: "none", "&:hover": { backgroundColor: "#163A63" } }}
            >
              Download PDF
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default StudentTrendChartDialog;
